<template>
  <div
    v-if="isEdit"
    class="flex h-80 max-w-full flex-col items-center justify-center space-y-3 border border-dashed border-gray-300 bg-gray-50"
    data-cy="upload-document"
    @drop.prevent="handleDrop"
    @dragenter.prevent
    @dragover.prevent
  >
    <div
      class="flex h-16 w-16 items-center justify-center rounded-full bg-blue-100"
    >
      <icon-base width="30" height="30" :icon="IconUploadFile" />
    </div>
    <lf-h3>
      {{ $t("CONTRACTS.CONTRACT_BUILDER.UPLOAD_CONTRACT") }}
    </lf-h3>
    <span class="w-40 text-center text-sm text-gray-500">
      {{
        $t("DEALS.DOCUMENTS.UPLOAD_TEXT_3", {
          docTypes: "pdf",
        })
      }}
      <input
        id="browse"
        type="file"
        class="hidden"
        accept=".pdf"
        :disabled="filesQueue.length > 0"
        @change="handleFileChange"
      />
      <label
        for="browse"
        class="cursor-pointer text-primary"
        data-cy="upload-document-input"
      >
        {{ $t("DEALS.DOCUMENTS.UPLOAD_TEXT_BROWSE") }}
      </label>
      {{ $t("DEALS.DOCUMENTS.UPLOAD_TEXT_2") }}
    </span>
  </div>
  <div
    v-else
    class="flex h-20 max-w-full flex-col items-center justify-center bg-gray-50"
    data-cy="save-first"
  >
    {{ $t("CONTRACTS.CONTRACT_BUILDER.SAVE_TO_UPLOAD") }}
  </div>
  <div v-if="filesQueue?.length">
    <div>
      <document-upload-item
        v-for="file in filesQueue"
        :key="file.id"
        allow-retry
        class="no-margin"
        :file="file"
        :upload-triggered="uploadTriggered"
        @file-cancelled="removeFileFromQueue"
        @file-retry="retryFileUpload"
      />
    </div>
  </div>
  <div
    class="flex min-w-full items-center justify-end space-x-4 border-t py-6 pr-5"
  >
    <div v-if="filesQueue.length > 1" class="text-error">
      {{ $t("CONTRACTS.CONTRACT_BUILDER.UPLOAD_ONE_FILE") }}
    </div>
    <primary-button
      data-cy="upload-document-confirm"
      :disabled="isUploadDisabled"
      @click="handleUploadClick"
    >
      {{ $t("DEALS.DOCUMENTS.UPLOAD") }}
    </primary-button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { areFilesExtensionsValid, getExtensionFromName } from "@/helpers/files";
import { makeRandomString } from "@/helpers/formatting";
import { useNotification } from "@/hooks/notifications";
import contractBuilderService from "@/services/modules/contractBuilder";
import { useContractBuilderStore } from "@/stores/contractBuilder";

import DocumentUploadItem from "@/components/deals/documents/DocumentUploadItem.vue";
import IconUploadFile from "@/components/icons/deals/IconUploadFile.vue";

import type { FileWBlob } from "@/models/common";

const emit = defineEmits<{
  "file:uploaded": [];
}>();

defineProps({
  isEdit: {
    type: Boolean,
    default: false,
  },
});

const ALLOWED_FILES_TYPES = ["pdf"];

const { showMessage } = useNotification();
const { t } = useI18n();
const store = useContractBuilderStore();
const { activeTemplate } = storeToRefs(store);

const filesQueue = ref<FileWBlob[]>([]);
const uploadTriggered = ref(false);

const isUploadDisabled = computed(
  () =>
    !filesQueue.value.filter((file) => !file.failed && !file.uploaded).length ||
    filesQueue.value.length > 1 ||
    uploadTriggered.value
);

const handleDrop = (dropEvent: DragEvent) => {
  if (dropEvent?.dataTransfer) {
    const fileArray = Object.values(dropEvent.dataTransfer.files);
    if (areFilesExtensionsValid(fileArray, ALLOWED_FILES_TYPES)) {
      enqueueFiles(dropEvent.dataTransfer.files);
    }
  }
};

const getFilesWithValidSize = (files: FileList | File[]) => {
  const filesArray = Object.values(files);
  const filesWithValidSize = filesArray.filter(
    (file) => Math.round(file.size / 1024) < 100_000
  );
  if (filesArray?.length !== filesWithValidSize?.length) {
    showMessage(
      t("DEALS.DOCUMENTS.FILE_BIG"),
      "error",
      t("DEALS.DOCUMENTS.FILE_BIG_SUBTITLE")
    );
  }
  return filesWithValidSize;
};

const handleFileChange = (e: Event) => {
  enqueueFiles((e?.target as HTMLInputElement)?.files);
  (e.target as HTMLInputElement).value = "";
};

const enqueueFiles = (files: FileList | File[] | null | undefined) => {
  if (!files) {
    return;
  }
  const toUpload = getFilesWithValidSize(files);
  filesQueue.value.push(
    ...toUpload.map((file) => {
      const type = getExtensionFromName(file.name);
      return {
        id: makeRandomString(),
        name: file.name,
        progress: 0,
        failed: false,
        uploaded: false,
        size: Math.round(file.size / 1024),
        type,
        documentType: "pdf",
        imagePreviewUrl: null,
        blob: file,
      };
    })
  );
};

const removeFileFromQueue = (id: string) => {
  const fileIndex = getIndexOfFileInQueue(id);

  if (!fileIndex.found) {
    return;
  }
  return filesQueue.value.splice(fileIndex.index, 1);
};

const getIndexOfFileInQueue = (id: string | undefined) => {
  if (!id) {
    return {
      index: -1,
      found: false,
    };
  }

  return {
    index: filesQueue.value.findIndex((file) => file.id === id),
    found: true,
  };
};

const retryFileUpload = async (id: string) => {
  const queueIndex = getIndexOfFileInQueue(id);
  if (!queueIndex.found) {
    return;
  }
  await uploadDocument(filesQueue.value[queueIndex.index]);
};

const handleUploadClick = async () => {
  if (filesQueue.value?.length) {
    uploadTriggered.value = true;
    filesQueue.value = filesQueue.value.filter((file) => !file.failed);

    const promises = filesQueue.value
      .filter((file) => !file.uploaded)
      .map((file) => uploadDocument(file));

    await Promise.all(promises).then(() => {
      uploadTriggered.value = false;
    });
  }
};

const uploadDocument = async (file: FileWBlob) => {
  const queueIndex = getIndexOfFileInQueue(file.id);

  if (
    !queueIndex.found ||
    !activeTemplate.value?.id ||
    filesQueue.value[queueIndex.index]?.uploaded
  ) {
    return;
  }
  try {
    filesQueue.value[queueIndex.index].failed = false;
    filesQueue.value[queueIndex.index].progress = 0;
    await contractBuilderService.uploadContractTemplateFile(
      activeTemplate.value.id,
      file.blob,
      {
        onUploadProgress: (progressEvent: ProgressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          filesQueue.value[queueIndex.index].progress = percentCompleted;
        },
      }
    );
    filesQueue.value[queueIndex.index].uploaded = true;
    emit("file:uploaded");
  } catch (error) {
    filesQueue.value[queueIndex.index].failed = true;
    return error;
  }
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

:deep(.no-margin) {
  @apply mx-0;
}
</style>
